/**
 * Performance Tab — Premium Daily Production Scorecard (PERF-STD-001, Sep 2026).
 * Personal metrics only; completions come standardized from /performance/detailed.
 */
import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Target, AlertTriangle } from 'lucide-react';
import { AnCard, AnPageHeader, AnEmptyState } from '../analytics/ui/AnalyticsUi';
import { usePerformanceMetrics } from './performance/usePerformanceMetrics';
import { PerformanceHeader } from './performance/PerformanceHeader';
import { DailyScorecard } from './performance/DailyScorecard';
import { PerformanceInsights } from './performance/PerformanceInsights';
import { WorkStatusSummary } from './performance/WorkStatusSummary';
import { CompletionTrend } from './performance/CompletionTrend';
import { WeekMonthPanel } from './performance/WeekMonthPanel';
import { QualitySnapshot } from './performance/QualitySnapshot';
import { PerformanceFooter } from './performance/PerformanceFooter';

const Skeleton = () => (
  <div className="space-y-4" data-testid="perf-skeleton" aria-hidden="true">
    <div className="flex justify-between"><div className="h-10 w-64 rounded-xl bg-slate-100 animate-pulse" /><div className="h-11 w-72 rounded-2xl bg-slate-100 animate-pulse" /></div>
    <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">{[0, 1, 2, 3].map(i => <div key={i} className="h-36 rounded-2xl bg-slate-100 animate-pulse" />)}</div>
    <div className="h-20 rounded-2xl bg-slate-200/70 animate-pulse" />
    <div className="grid lg:grid-cols-5 gap-4"><div className="lg:col-span-3 h-44 rounded-2xl bg-slate-100 animate-pulse" /><div className="lg:col-span-2 h-44 rounded-2xl bg-slate-100 animate-pulse" /></div>
    <div className="grid lg:grid-cols-5 gap-4"><div className="lg:col-span-3 h-80 rounded-2xl bg-slate-100 animate-pulse" /><div className="lg:col-span-2 h-80 rounded-2xl bg-slate-100 animate-pulse" /></div>
  </div>
);

const PerformanceTab = ({ currentUser }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [asOf, setAsOf] = useState(() => searchParams.get('date') || null);
  const { metrics, loading, switching, error, refetch } = usePerformanceMetrics(asOf);

  const changeDate = (iso) => {
    setAsOf(iso);
    const next = new URLSearchParams(searchParams);
    if (iso) next.set('date', iso); else next.delete('date');
    setSearchParams(next, { replace: true });
  };

  if (loading && !metrics) return <Skeleton />;

  if (!metrics?.can_view) {
    return (
      <AnCard className="p-8" testId="perf-unavailable">
        <AnEmptyState tone="neutral" icon={<AlertTriangle className="h-5 w-5" />} title={error || 'Performance data not available'}
          subtitle="Performance tracking is available for Processors and Team Leaders only." action={error ? <button type="button" onClick={refetch} className="an-btn rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50" data-testid="perf-retry">Retry</button> : null} />
      </AnCard>
    );
  }

  return (
    <div className="space-y-4" data-testid="performance-tab" data-as-of={metrics.as_of_date}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <AnPageHeader icon={Target} title="Performance" desc="Your daily production, quality and progress" testId="perf-page-header" />
        <PerformanceHeader m={metrics} asOf={asOf} onChangeDate={changeDate} switching={switching} />
      </div>
      {error && <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800" data-testid="perf-stale-notice">{error} Showing last loaded data.</div>}
      <div className={`pf-fade space-y-4 ${switching ? 'opacity-70' : ''}`} key={metrics.as_of_date}>
        <DailyScorecard m={metrics} />
        <PerformanceInsights m={metrics} />
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          <div className="lg:col-span-3"><WorkStatusSummary m={metrics} role={currentUser?.role} /></div>
          <div className="lg:col-span-2"><QualitySnapshot errorsThisWeek={metrics.errors_this_week} errorsBreakdown={metrics.errors_breakdown} /></div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          <div className="lg:col-span-3"><CompletionTrend history={metrics.daily_history} target={metrics.daily_target} switching={switching} /></div>
          <div className="lg:col-span-2"><WeekMonthPanel m={metrics} /></div>
        </div>
        <PerformanceFooter m={metrics} />
      </div>
    </div>
  );
};

export default PerformanceTab;
